import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

export interface DashboardProfile {
  nome: string;
  fotoUrl: string;
}

interface DashboardProfileContextValue {
  profile: DashboardProfile | null;
  loading: boolean;
  /** Chamado pelo AccountDialog depois de salvar, pra UserMenu/ModuleToolbar
   *  refletirem o nome/foto novos sem precisar de um listener em tempo real. */
  refresh: () => Promise<void>;
}

const DashboardProfileContext = createContext<DashboardProfileContextValue | null>(null);

export function DashboardProfileProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState<DashboardProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user || !db) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'dashboardUsers', user.uid));
      setProfile(snap.exists() ? (snap.data() as DashboardProfile) : null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <DashboardProfileContext.Provider value={{ profile, loading, refresh: load }}>
      {children}
    </DashboardProfileContext.Provider>
  );
}

export function useDashboardProfile() {
  const context = useContext(DashboardProfileContext);
  if (!context) {
    throw new Error('useDashboardProfile deve ser usado dentro de DashboardProfileProvider');
  }
  return context;
}
